// ─────────────────────────────────────────────────────────────────────────────
// utils/listPagination.ts — Blaettern statt Kuerzen in Drucker- und
// Mandantenliste (T-M8.19b)
//
// REQ-OPS-032 (in der Fassung vom 2026-08-17): die Druckerliste und
// gleichgeartete Geraete-/Mandantenlisten laden hoechstens 100 Eintraege JE
// SEITE und blaettern bzw. laden nach — die erreichbare Menge ist NICHT
// begrenzt. Die 100 sind ab jetzt die Seitengroesse, nicht die Obergrenze.
// Dazu REQ-DM-050/050a (deterministische, ueber Seitengrenzen hinweg stabile
// Reihenfolge samt Sortierrichtung) und REQ-PS-021 (Live-Ueberwachung).
//
// ── WARUM ES DIESE DATEI GIBT ───────────────────────────────────────────────
// Bis zum 2026-08-17 kuerzten beide Listen bei 100 und sagten es (T-M8.19a).
// Das war sichtbar, aber eine Sackgasse: „aktuell gibt es keine Moeglichkeit
// mehr als die ersten 100 Eintraege anzusehen oder zu waehlen" (Auftraggeber,
// 2026-08-17). Der Hinweis wird deshalb zum Bedienelement — und die Regeln der
// Seitengrenze brauchen einen Ort, an dem sie OHNE Mount und ohne Firestore
// pruefbar sind.
//
// ── KEINE ZWEITE ERFINDUNG ──────────────────────────────────────────────────
// Die Bauform ist woertlich die der Auftragsliste (`utils/jobPagination.ts`,
// T-M9.3, REQ-OPS-033): Live-Seite plus statische Folgeseiten, Cursor per
// `startAfter`, Zusammenfuehren ueber alle Quellen in der Abfragereihenfolge,
// Rueckhalt der unter die Live-Seite abgesunkenen Zeilen. Verschieden ist
// AUSSCHLIESSLICH die Ordnung: dort `createdAt` ABSTEIGEND, hier `name`
// AUFSTEIGEND mit `__name__`-Tiebreak in derselben Richtung. Die Ordnung ist
// der Grund, warum dies eine eigene Datei ist und keine Wiederverwendung: der
// Vergleich ist der Kern, an dem Doppel- und Fehlanzeigen entstehen, und ein
// Vergleich, der beide Faelle ueber einen Schalter bedient, waere an genau
// dieser Stelle schwerer zu lesen als zwei kurze.
//
// ── DER ZUSAMMENHANG MIT `listTruncation.ts` (T-M8.19a) ─────────────────────
// Die Regel „eine volle Seite kann mehr verbergen" ist DIESELBE wie die Regel
// „eine Liste an ihrer Grenze kann gekuerzt sein" — deshalb ruft `canLoadMore`
// unten `isListTruncated` und baut sie nicht nach. Was sich geaendert hat, ist
// nicht die Regel, sondern was aus ihr folgt: frueher ein Hinweis, jetzt ein
// Nachlade-Bedienelement.
// ─────────────────────────────────────────────────────────────────────────────

import { isListTruncated } from './listTruncation';

/** Der fuer die Seitenlogik gelesene Teil eines Drucker-/Firmen-Dokuments. */
export interface PagedNamedLike {
  id: string;
  /**
   * Bezeichnung — das Sortierfeld der Abfrage (aufsteigend nach `name`). Fehlt sie,
   * gilt der Eintrag als leerer String; Firestore liefert Dokumente ohne das
   * Sortierfeld ohnehin nicht aus.
   */
  name?: string | null;
}

function nameOf(entry: PagedNamedLike): string {
  return typeof entry.name === 'string' ? entry.name : '';
}

/**
 * Vergleich in der Reihenfolge der Abfrage: `name` AUFSTEIGEND, danach die
 * Dokument-ID AUFSTEIGEND.
 *
 * Der zweite Schluessel ist kein Zusatz, sondern Firestores eigener Tiebreak:
 * eine aufsteigend nach `name` sortierte Abfrage sortiert bei Gleichstand
 * implizit nach `__name__` in DERSELBEN Richtung. Wer clientseitig anders
 * sortiert, verschiebt die Seitengrenze gegenueber dem Cursor — und genau dort
 * entstehen Doppel- und Fehlanzeigen (REQ-DM-050a).
 *
 * Verglichen wird wie Firestore: Zeichenkette gegen Zeichenkette, ohne
 * Gebietsschema. Ein `localeCompare` waere hier falsch — es sortierte „Ärger"
 * vor „Bar", waehrend die Datenbank ihn dahinter einordnet, und schoebe damit
 * Eintraege ueber die Seitengrenze.
 */
export function compareNamedByQueryOrder(a: PagedNamedLike, b: PagedNamedLike): number {
  const na = nameOf(a);
  const nb = nameOf(b);
  if (na !== nb) return na < nb ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;   // ID aufsteigend
}

/**
 * Steht `entry` in der Abfragereihenfolge HINTER `reference` (also weiter unten
 * in der Liste)?
 */
export function isAfter(entry: PagedNamedLike, reference: PagedNamedLike): boolean {
  return compareNamedByQueryOrder(entry, reference) > 0;
}

/**
 * Die anzuzeigende Liste aus den drei Quellen — Live-Seite, nachgeladene Seiten
 * und die unter die Live-Seite abgesunkenen Zeilen.
 *
 * Doppelte Kennungen werden hier aufgeloest, die Live-Seite gewinnt. Sortiert
 * wird ueber ALLE Quellen hinweg nach der Abfragereihenfolge, nicht bloss
 * angehaengt: nur so bleibt die Reihenfolge ueber Seitengrenzen hinweg stabil,
 * auch wenn waehrend des Blaetterns ein Drucker angelegt oder umbenannt wird.
 */
export function mergeNamedPages<T extends PagedNamedLike>(
  livePage: readonly T[],
  staticPages: readonly (readonly T[])[] = [],
  retained: readonly T[] = [],
): T[] {
  const byId = new Map<string, T>();
  for (const entry of [...livePage, ...staticPages.flat(), ...retained]) {
    if (!byId.has(entry.id)) byId.set(entry.id, entry);
  }
  return [...byId.values()].sort(compareNamedByQueryOrder);
}

/**
 * Welche bereits angezeigten Zeilen sind nach einer Live-Aktualisierung zu
 * BEHALTEN, obwohl sie nicht mehr in der Live-Seite stehen?
 *
 * Das Problem, das damit geschlossen wird — dieselbe Luecke wie in der
 * Auftragsliste: die nachgeladenen Seiten haengen an einem Cursor
 * (`startAfter` auf dem damals letzten Dokument der Live-Seite). Wird danach
 * ein Drucker angelegt, dessen Bezeichnung alphabetisch VOR das Seitenende
 * faellt, rutscht die Live-Seite nach; ihr damals letztes Dokument steht dann
 * WEDER in der Live-Seite NOCH in Seite 2 — es waere eine Luecke mitten in der
 * Liste. Solche abgesunkenen Zeilen werden deshalb weitergefuehrt. Sie sind ab
 * diesem Moment STATISCH, aus demselben Grund wie eine nachgeladene Seite.
 *
 * Zwei Bedingungen verhindern Karteileichen:
 *   1. Nur wenn ueberhaupt nachgeladen wurde. Ohne Folgeseiten gibt es keine
 *      Luecke zu schliessen — ein geloeschter Drucker verschwindet dann richtig.
 *   2. Nur Zeilen HINTER dem letzten Dokument der Live-Seite. Ein GELOESCHTER
 *      Drucker innerhalb des Live-Fensters liegt nicht dahinter und verschwindet
 *      damit richtigerweise.
 * Eine nicht volle Live-Seite kann nichts hinausschieben — dann wird ebenfalls
 * nichts behalten.
 */
export function retainedAfterNamedLiveUpdate<T extends PagedNamedLike>(
  previouslyDisplayed: readonly T[],
  livePage: readonly T[],
  staticPages: readonly (readonly T[])[],
  pageSize: number,
): T[] {
  if (staticPages.length === 0) return [];
  if (livePage.length < pageSize) return [];

  const tail = livePage[livePage.length - 1];
  if (!tail) return [];

  const known = new Set<string>();
  for (const entry of [...livePage, ...staticPages.flat()]) known.add(entry.id);

  return previouslyDisplayed.filter(e => !known.has(e.id) && isAfter(e, tail));
}

/**
 * Kann noch weiter nachgeladen werden?
 *
 * Massgeblich ist die zuletzt GELADENE Seite: eine volle Seite bedeutet, dass
 * dahinter weitere Eintraege liegen koennen; eine kurze Seite ist das Ende der
 * Liste. Solange nichts nachgeladen wurde (`lastPageSize === null`), entscheidet
 * die Live-Seite.
 *
 * Die Regel ist WOERTLICH die des Kuerzungshinweises aus T-M8.19a und wird
 * deshalb von dort geholt statt nachgebaut (`isListTruncated`): „so viele
 * geladen, wie die Abfrage hoechstens liefern darf" heisst seit dem
 * 2026-08-17 nicht mehr „hier ist Schluss", sondern „hier geht es weiter".
 *
 * Bewusst grosszuegig: bei einer exakt aufgehenden Gesamtzahl (genau 100
 * Drucker) meldet die Funktion einmal zu viel „es gibt mehr". Der naechste
 * Ladeversuch liefert dann null Eintraege und beendet die Liste sichtbar. Der
 * umgekehrte Fehler waere schwerer: eine still verschwiegene letzte Seite —
 * genau der Schaden, gegen den REQ-OPS-032 gebaut ist.
 */
export function canLoadMoreNamed(
  livePageSize: number,
  lastPageSize: number | null,
  pageSize: number,
): boolean {
  return isListTruncated(lastPageSize ?? livePageSize, pageSize);
}
